import { useState, useRef, useEffect } from "react";
import { FaPaperPlane, FaRobot, FaUser, FaTrash } from "react-icons/fa";
import Markdown from "markdown-to-jsx";
import Column from "components/ui/layout/column/column";
import Row from "components/ui/layout/row/row";
import CardBox from "components/ui/controls/card-box/card-box";
import Button from "components/ui/controls/button/button";
import "./AssistantChatbot.css";

const AssistantChatbot = () => {
  // Détection automatique de l'URL du serveur
  // En production, utilise le même domaine via reverse proxy nginx
  const API_BASE_URL =
    process.env.REACT_APP_VERTEX_API_URL || (window.location.hostname === "localhost" ? "http://localhost:3001" : `${window.location.protocol}//${window.location.hostname}`);

  const [messages, setMessages] = useState([
    {
      id: 1,
      type: "bot",
      text: "Bonjour ! Je suis votre assistant MAKO. Comment puis-je vous aider aujourd'hui ?",
      timestamp: new Date(),
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;

    const userMessage = {
      id: messages.length + 1,
      type: "user",
      text: inputMessage,
      timestamp: new Date(),
    };

    setMessages([...messages, userMessage]);
    const questionToSend = inputMessage;
    setInputMessage("");
    setIsTyping(true);

    // Créer un message bot vide qui sera mis à jour progressivement
    const botMessageId = messages.length + 2;
    const initialBotMessage = {
      id: botMessageId,
      type: "bot",
      text: "",
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, initialBotMessage]);
    setIsTyping(false);
    setStreamingMessageId(botMessageId); // Marquer ce message comme en streaming

    try {
      // Appel au backend Vertex AI avec streaming
      const response = await fetch(`${API_BASE_URL}/api/vertex-ai/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          question: questionToSend,
        }),
      });

      if (!response.ok) {
        throw new Error("Erreur lors de la communication avec l'assistant");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      const processStream = async () => {
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            if (buffer.length > 0) {
              handleMessage(buffer);
            }
            setStreamingMessageId(null);
            break;
          }

          buffer += decoder.decode(value, { stream: true });
          let boundary;
          while ((boundary = buffer.indexOf("\n\n")) !== -1) {
            const message = buffer.slice(0, boundary);
            buffer = buffer.slice(boundary + 2);
            if (message) {
              handleMessage(message);
            }
          }
        }
      };

      let accumulatedText = "";
      const handleMessage = (message) => {
        const lines = message.split("\n");
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.error) {
                throw new Error(data.error);
              }
              if (data.text) {
                accumulatedText += data.text;
                setMessages((prev) => prev.map((msg) => (msg.id === botMessageId ? { ...msg, text: accumulatedText } : msg)));
              }
              if (data.done) {
                setStreamingMessageId(null);
              }
            } catch (e) {
              console.error("Error parsing SSE data:", e);
            }
          }
        }
      };

      await processStream();
    } catch (error) {
      console.error("Erreur API Assistant:", error);
      setStreamingMessageId(null);
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === botMessageId
            ? {
                ...msg,
                text: "Désolé, je rencontre des difficultés pour répondre à votre question. Veuillez réessayer dans quelques instants.",
              }
            : msg
        )
      );
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    setMessages([
      {
        id: 1,
        type: "bot",
        text: "Bonjour ! Je suis votre assistant MAKO. Comment puis-je vous aider aujourd'hui ?",
        timestamp: new Date(),
      },
    ]);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  };

  // Convertir les formules mathématiques KaTeX en HTML avec balises <b>
  const convertMathToHtml = (text) => {
    // Remplacer les formules $$...$$ par du HTML avec <b>
    // et supprimer les sauts de ligne avant et après
    return text.replace(/\n*\$\$(.*?)\$\$\n*/g, (match, formula) => {
      // Extraire les termes entre \text{...}
      let htmlFormula = formula.replace(/\\text\{([^}]+)\}/g, "<b>$1</b>");
      // Remplacer \times par " x " (avec espaces)
      htmlFormula = htmlFormula.replace(/\\times/g, " x ");
      // Nettoyer les espaces multiples mais garder les espaces simples
      htmlFormula = htmlFormula.replace(/\s+/g, " ").trim();
      // Ajouter un espace avant la formule pour la séparer du texte précédent
      return " " + htmlFormula;
    });
  };

  const processNewlines = (text) => {
    return text.replace(/\\n/g, "\n");
  };

  return (
    <Column>
      <Row>
        <Column>
          <CardBox fill className="chatbot-container">
            <div className="chatbot-wrapper">
              {/* Messages Area */}
              <div className="chatbot-messages">
                {messages.map((message) => (
                  <div key={message.id} className={`chatbot-message ${message.type === "user" ? "user-message" : "bot-message"}`}>
                    <div className="message-avatar">{message.type === "user" ? <FaUser /> : <FaRobot />}</div>
                    <div className="message-content">
                      <div className={`message-text ${message.id === streamingMessageId ? "streaming" : ""}`}>
                        <Markdown>{processNewlines(convertMathToHtml(message.text))}</Markdown>
                      </div>
                      <div className="message-time">{formatTime(message.timestamp)}</div>
                    </div>
                  </div>
                ))}
                {isTyping && (
                  <div className="chatbot-message bot-message">
                    <div className="message-avatar">
                      <FaRobot />
                    </div>
                    <div className="message-content">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="chatbot-input-container">
                <div className="chatbot-input-wrapper">
                  <textarea
                    ref={inputRef}
                    className="chatbot-input"
                    placeholder="Posez votre question..."
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    rows={1}
                  />
                  <button className="chatbot-send-button" onClick={handleSendMessage} disabled={inputMessage.trim() === ""}>
                    <FaPaperPlane />
                  </button>
                </div>
                <div className="chatbot-input-hint">Appuyez sur Entrée pour envoyer • Maj+Entrée pour un retour à la ligne</div>
              </div>
            </div>
          </CardBox>
        </Column>
      </Row>
    </Column>
  );
};

export default AssistantChatbot;
